import React, { useState, useEffect } from "react";
import Header from "../layout/Header";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Table, { TableRow, TableCell } from "../common/Table";
import {
  getAgreements,
  addAgreement,
  updateAgreement,
  deleteAgreement,
} from "../../firebase/firestore";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  FileText,
  ExternalLink,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  X,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const Agreements = () => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterRequired, setFilterRequired] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const [newAgreement, setNewAgreement] = useState({
    title: "",
    isRequired: false,
    order: 0,
    file: null,
  });

  const [editForm, setEditForm] = useState({
    title: "",
    isRequired: false,
    order: 0,
    file: null,
  });

  const itemsPerPageOptions = [5, 10, 25, 50, 100];

  useEffect(() => {
    loadAgreements();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRequired]);

  const showToast = (type, message) => {
    setToast({
      show: true,
      type,
      message,
    });

    setTimeout(() => {
      setToast({
        show: false,
        type: "success",
        message: "",
      });
    }, 3000);
  };

  const loadAgreements = async () => {
    try {
      setLoading(true);
      const data = await getAgreements();
      setAgreements(data);
    } catch (error) {
      console.error("Error loading agreements:", error);
      showToast("error", "Failed to load agreements.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddAgreement = async (e) => {
    e.preventDefault();

    if (!newAgreement.file) {
      showToast("error", "Please select a PDF file.");
      return;
    }

    try {
      setActionLoading(true);

      const fd = new FormData();
      fd.append("file", newAgreement.file);
      fd.append("title", newAgreement.title);
      fd.append("isRequired", newAgreement.isRequired);
      fd.append("order", newAgreement.order);
      fd.append("createdAt", new Date().toISOString());

      await addAgreement(fd);

      setNewAgreement({
        title: "",
        isRequired: false,
        order: 0,
        file: null,
      });

      setIsAddModalOpen(false);
      await loadAgreements();

      showToast("success", "Agreement added successfully.");
    } catch (error) {
      console.error("Error adding agreement:", error);
      showToast("error", error.message || "Failed to add agreement.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditAgreement = async (e) => {
    e.preventDefault();

    try {
      setActionLoading(true);

      const fd = new FormData();

      if (editForm.file) {
        fd.append("file", editForm.file);
      }

      fd.append("title", editForm.title);
      fd.append("isRequired", editForm.isRequired);
      fd.append("order", editForm.order);
      fd.append("updatedAt", new Date().toISOString());

      await updateAgreement(editingAgreement.id, fd);

      setEditingAgreement(null);
      setEditForm({
        title: "",
        isRequired: false,
        order: 0,
        file: null,
      });

      await loadAgreements();

      showToast("success", "Agreement updated successfully.");
    } catch (error) {
      console.error("Error updating agreement:", error);
      showToast("error", error.message || "Failed to update agreement.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAgreement = async (id) => {
    if (!window.confirm("Are you sure you want to delete this agreement?")) {
      return;
    }

    try {
      setActionLoading(true);

      await deleteAgreement(id);
      await loadAgreements();

      showToast("success", "Agreement deleted successfully.");
    } catch (error) {
      console.error("Error deleting agreement:", error);
      showToast("error", error.message || "Failed to delete agreement.");
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = (agreement) => {
    setEditingAgreement(agreement);
    setEditForm({
      title: agreement.title || "",
      isRequired: agreement.isRequired || false,
      order: agreement.order || 0,
      file: null,
    });
  };

  const filteredAgreements = agreements.filter((a) => {
    const matchesSearch = a.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterRequired === "all" ||
      (filterRequired === "required" && a.isRequired) ||
      (filterRequired === "optional" && !a.isRequired);

    return matchesSearch && matchesFilter;
  });

  const totalItems = filteredAgreements.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredAgreements.slice(startIndex, endIndex);

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () =>
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPage = (page) => setCurrentPage(page);

  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    const startPage = Math.max(1, currentPage - delta);
    const endPage = Math.min(totalPages, currentPage + delta);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Message */}
      {toast.show && (
        <div className="fixed top-5 right-5 z-[9999]">
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border bg-white ${
              toast.type === "success"
                ? "border-green-200 text-green-700"
                : "border-red-200 text-red-700"
            }`}
          >
            {toast.type === "success" ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}

            <span className="text-sm font-medium">{toast.message}</span>

            <button
              onClick={() =>
                setToast({
                  show: false,
                  type: "success",
                  message: "",
                })
              }
              className="ml-2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Action Loader Overlay */}
      {actionLoading && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[9998]">
          <div className="bg-white rounded-xl px-6 py-4 shadow-lg flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
            <span className="text-sm font-medium text-gray-700">
              Please wait...
            </span>
          </div>
        </div>
      )}

      <Header
        title="Agreements Management"
        subtitle="Upload and manage coach agreement documents"
      />

      <div className="glass rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              All Agreements ({totalItems})
            </h3>

            {searchTerm && (
              <p className="text-sm text-gray-600 mt-1">
                Showing {currentItems.length} of {totalItems} filtered results
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="relative">
              <Search
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                placeholder="Search agreements..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
              />
            </div>

            <select
              value={filterRequired}
              onChange={(e) => setFilterRequired(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Types</option>
              <option value="required">Required</option>
              <option value="optional">Optional</option>
            </select>

            <Button
              onClick={() => setIsAddModalOpen(true)}
              disabled={actionLoading}
            >
              <Plus size={20} className="mr-2" />
              Add Agreement
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Show:</label>

            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <span className="text-sm text-gray-600">entries</span>
          </div>

          {totalPages > 1 && (
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of{" "}
              {totalItems} entries
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <Table
            headers={[
              "Title",
              "Version",
              "Required",
              "Order",
              "PDF",
              "Created",
              "Updated",
              "Actions",
            ]}
          >
            {currentItems.map((agreement) => (
              <TableRow key={agreement.id}>
                <TableCell className="font-medium">
                  {agreement.title}
                </TableCell>

                <TableCell>
                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    v{agreement.version}
                  </span>
                </TableCell>

                <TableCell>
                  {agreement.isRequired ? (
                    <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Required
                    </span>
                  ) : (
                    <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Optional
                    </span>
                  )}
                </TableCell>

                <TableCell>{agreement.order}</TableCell>

                <TableCell>
                  {agreement.fileUrl ? (
                    <a
                      href={agreement.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 text-sm"
                    >
                      <FileText size={14} />
                      <span>View</span>
                      <ExternalLink size={12} />
                    </a>
                  ) : (
                    <span className="text-gray-400 text-sm">No file</span>
                  )}
                </TableCell>

                <TableCell className="text-sm text-gray-600">
                  {agreement.createdAt
                    ? new Date(agreement.createdAt).toLocaleDateString()
                    : "N/A"}
                </TableCell>

                <TableCell className="text-sm text-gray-600">
                  {agreement.updatedAt && agreement.updatedAt !== "-"
                    ? new Date(agreement.updatedAt).toLocaleDateString()
                    : "-"}
                </TableCell>

                <TableCell>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(agreement)}
                      disabled={actionLoading}
                      className="p-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      title="Edit Agreement"
                    >
                      <Edit size={16} />
                    </button>

                    <button
                      onClick={() => handleDeleteAgreement(agreement.id)}
                      disabled={actionLoading}
                      className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                      title="Delete Agreement"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </Table>
        </div>

        {currentItems.length === 0 && (
          <div className="text-center py-12">
            <FileText size={48} className="mx-auto text-gray-400 mb-3" />

            <p className="text-gray-600 mb-2">
              {searchTerm
                ? "No agreements found matching your search."
                : "No agreements found. Add your first agreement!"}
            </p>

            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-primary-600 hover:text-primary-800 text-sm"
              >
                Clear search to see all agreements
              </button>
            )}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages} ({totalItems} total entries)
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First Page"
              >
                <ChevronsLeft size={16} />
              </button>

              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous Page"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="flex items-center gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      pageNum === currentPage
                        ? "bg-primary-500 text-white"
                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next Page"
              >
                <ChevronRight size={16} />
              </button>

              <button
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last Page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Agreement Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => !actionLoading && setIsAddModalOpen(false)}
        title="Add New Agreement"
        size="md"
      >
        <form onSubmit={handleAddAgreement} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>

            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none"
              value={newAgreement.title}
              onChange={(e) =>
                setNewAgreement({
                  ...newAgreement,
                  title: e.target.value,
                })
              }
              required
              disabled={actionLoading}
              placeholder="e.g. Coach Agreement"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order
              </label>

              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none"
                value={newAgreement.order}
                onChange={(e) =>
                  setNewAgreement({
                    ...newAgreement,
                    order: e.target.value,
                  })
                }
                disabled={actionLoading}
                placeholder="Display order"
              />
            </div>

            <div className="flex items-center pt-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() =>
                    setNewAgreement({
                      ...newAgreement,
                      isRequired: !newAgreement.isRequired,
                    })
                  }
                  className={`relative w-10 h-5 rounded-full transition-colors disabled:opacity-50 ${
                    newAgreement.isRequired
                      ? "bg-primary-500"
                      : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      newAgreement.isRequired
                        ? "translate-x-5"
                        : "translate-x-0"
                    }`}
                  />
                </button>

                <span className="text-sm font-medium text-gray-700">
                  Required
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PDF File *
            </label>

            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) =>
                setNewAgreement({
                  ...newAgreement,
                  file: e.target.files[0],
                })
              }
              required
              disabled={actionLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />

            <p className="text-xs text-gray-500 mt-1">Max 10MB, PDF only</p>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button
              type="button"
              variant="secondary"
              disabled={actionLoading}
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>

            <Button type="submit" disabled={actionLoading}>
              {actionLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  Adding...
                </span>
              ) : (
                "Add"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Agreement Modal */}
      {editingAgreement && (
        <Modal
          isOpen={!!editingAgreement}
          onClose={() => !actionLoading && setEditingAgreement(null)}
          title="Edit Agreement"
          size="md"
        >
          <form onSubmit={handleEditAgreement} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>

              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none"
                value={editForm.title}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    title: e.target.value,
                  })
                }
                required
                disabled={actionLoading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order
                </label>

                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none"
                  value={editForm.order}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      order: e.target.value,
                    })
                  }
                  disabled={actionLoading}
                />
              </div>

              <div className="flex items-center pt-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() =>
                      setEditForm({
                        ...editForm,
                        isRequired: !editForm.isRequired,
                      })
                    }
                    className={`relative w-10 h-5 rounded-full transition-colors disabled:opacity-50 ${
                      editForm.isRequired
                        ? "bg-primary-500"
                        : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        editForm.isRequired
                          ? "translate-x-5"
                          : "translate-x-0"
                      }`}
                    />
                  </button>

                  <span className="text-sm font-medium text-gray-700">
                    Required
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PDF File{" "}
                <span className="text-gray-400 font-normal">
                  (leave empty to keep current)
                </span>
              </label>

              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    file: e.target.files[0],
                  })
                }
                disabled={actionLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:outline-none file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />

              <p className="text-xs text-gray-500 mt-1">
                Max 10MB, PDF only. Version will auto-increment on save.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button
                type="button"
                variant="secondary"
                disabled={actionLoading}
                onClick={() => setEditingAgreement(null)}
              >
                Cancel
              </Button>

              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    Updating...
                  </span>
                ) : (
                  "Update"
                )}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Agreements;